import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RefreshCw, Search, Shield, Building2 } from 'lucide-react';
import {
  getPlatformStats,
  getPlatformOrganizations,
  getPlatformOrgDetail,
  getPlatformAuditLog,
} from '../lib/api';

interface PlatformStats {
  active_organizations: number;
  total_users: number;
  active_subscriptions: number;
  posts_last_30_days: number;
  image_regens_last_30_days: number;
  estimated_revenue_last_30_days_cents: number;
}

interface OrgSummary {
  id: string;
  name: string;
  slug: string;
  subscription_status: string;
  included_posts: number;
  member_count: number;
  posts_this_period: number;
  overage_posts: number;
  image_regens_this_period: number;
  upcoming_invoice_cents: number | null;
}

interface AuditEvent {
  id: string;
  timestamp: string;
  event_type: string;
  org_name: string;
  user_email: string;
  site_id: string | null;
  cost_cents: number;
  stripe_reported: boolean;
}

type View = 'overview' | 'org-detail' | 'audit';

export default function SuperAdminDashboard() {
  const toast = useToast();
  const [view, setView] = useState<View>('overview');
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditFilter, setAuditFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fmtCost = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([getPlatformStats(), getPlatformOrganizations()]);
      setStats(s);
      setOrgs(o.organizations || []);
    } catch {
      toast({ title: 'Failed to load platform data', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadOrgDetail = useCallback(async (orgId: string) => {
    setLoading(true);
    try {
      const detail = await getPlatformOrgDetail(orgId);
      setSelectedOrg(detail);
      setView('org-detail');
    } catch {
      toast({ title: 'Failed to load org details', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadAuditLog = useCallback(async (orgId?: string) => {
    setLoading(true);
    try {
      const data = await getPlatformAuditLog({ org_id: orgId, limit: 200 });
      setAuditEvents(data.events || []);
      setView('audit');
    } catch {
      toast({ title: 'Failed to load audit log', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  if (loading && !stats) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" color="purple.500" />
        <Text mt={4} color="gray.500">Loading platform admin dashboard...</Text>
      </Box>
    );
  }

  if (view === 'org-detail' && selectedOrg) {
    const org = selectedOrg.organization;
    return (
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <HStack>
            <Button size="sm" variant="ghost" onClick={() => { setView('overview'); setSelectedOrg(null); }}>
              ← Back
            </Button>
            <Text fontSize="xl" fontWeight="700">{org.name}</Text>
            <Badge colorScheme={org.subscription_status === 'active' ? 'green' : 'gray'}>
              {org.subscription_status}
            </Badge>
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Included Posts</StatLabel>
              <StatNumber>{org.included_posts}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Members</StatLabel>
              <StatNumber>{selectedOrg.members?.length || 0}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Upcoming Invoice</StatLabel>
              <StatNumber>{org.upcoming_invoice_cents ? fmtCost(org.upcoming_invoice_cents) : '—'}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Period</StatLabel>
              <StatNumber fontSize="sm">
                {org.billing_period_start ? new Date(org.billing_period_start).toLocaleDateString() : '—'}
                {' — '}
                {org.billing_period_end ? new Date(org.billing_period_end).toLocaleDateString() : '—'}
              </StatNumber>
            </Stat>
          </SimpleGrid>

          {/* Members */}
          <Box bg="white" p={4} borderRadius="lg" borderWidth={1}>
            <Text fontWeight="600" mb={3}>Members</Text>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Name / Email</Th>
                  <Th>Role</Th>
                  <Th>Joined</Th>
                  <Th>Last Active</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(selectedOrg.members || []).map((m: any) => (
                  <Tr key={m.user_id}>
                    <Td>
                      <Text fontWeight="500">{m.display_name}</Text>
                      <Text fontSize="xs" color="gray.500">{m.email}</Text>
                    </Td>
                    <Td><Badge>{m.role}</Badge></Td>
                    <Td fontSize="xs">{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '—'}</Td>
                    <Td fontSize="xs">{m.last_active_at ? new Date(m.last_active_at).toLocaleDateString() : '—'}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Usage by User */}
          <Box bg="white" p={4} borderRadius="lg" borderWidth={1}>
            <Text fontWeight="600" mb={3}>Usage by User</Text>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Email</Th>
                  <Th isNumeric>Posts</Th>
                  <Th isNumeric>Regens</Th>
                  <Th isNumeric>Cost</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(selectedOrg.usage_by_user || []).map((u: any) => (
                  <Tr key={u.user_id || u.email}>
                    <Td>{u.email}</Td>
                    <Td isNumeric>{u.posts_generated}</Td>
                    <Td isNumeric>{u.image_regens}</Td>
                    <Td isNumeric>{fmtCost(u.total_cost_cents)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

          {/* Daily Usage Chart */}
          {selectedOrg.daily_usage?.length > 0 && (
            <Box bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <Text fontWeight="600" mb={3}>Daily Usage (30 Days)</Text>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={selectedOrg.daily_usage}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="posts" fill="#805AD5" name="Posts" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="image_regens" fill="#D6BCFA" name="Regens" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          <Button size="sm" variant="outline" onClick={() => loadAuditLog(org.id)}>
            View Full Audit Log →
          </Button>
        </VStack>
      </Box>
    );
  }

  if (view === 'audit') {
    const filtered = auditFilter
      ? auditEvents.filter(e =>
          e.user_email.toLowerCase().includes(auditFilter.toLowerCase()) ||
          e.org_name.toLowerCase().includes(auditFilter.toLowerCase()) ||
          e.event_type.includes(auditFilter.toLowerCase())
        )
      : auditEvents;

    return (
      <Box p={6}>
        <VStack spacing={4} align="stretch">
          <HStack>
            <Button size="sm" variant="ghost" onClick={() => setView('overview')}>← Back</Button>
            <Text fontSize="xl" fontWeight="700">Audit Log</Text>
          </HStack>

          <InputGroup size="sm" maxW="400px">
            <InputLeftElement><Search size={14} /></InputLeftElement>
            <Input placeholder="Filter by email, org, or event type..." value={auditFilter} onChange={e => setAuditFilter(e.target.value)} />
          </InputGroup>

          <Box overflowX="auto" bg="white" borderRadius="lg" borderWidth={1}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Time</Th>
                  <Th>Event</Th>
                  <Th>Org</Th>
                  <Th>User</Th>
                  <Th>Site</Th>
                  <Th isNumeric>Cost</Th>
                  <Th>Reported</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map(e => (
                  <Tr key={e.id}>
                    <Td fontSize="xs">{e.timestamp ? new Date(e.timestamp).toLocaleString() : '—'}</Td>
                    <Td><Badge size="sm" variant="subtle" colorScheme={e.event_type.includes('image') ? 'purple' : 'brand'}>{e.event_type}</Badge></Td>
                    <Td fontSize="sm">{e.org_name}</Td>
                    <Td fontSize="xs">{e.user_email}</Td>
                    <Td fontSize="xs">{e.site_id || '—'}</Td>
                    <Td isNumeric fontSize="xs">{e.cost_cents > 0 ? fmtCost(e.cost_cents) : '—'}</Td>
                    <Td>{e.stripe_reported ? <Badge colorScheme="green" size="sm">✓</Badge> : <Badge colorScheme="gray" size="sm">—</Badge>}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      </Box>
    );
  }

  // Overview
  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Shield size={20} />
            <Text fontSize="xl" fontWeight="700">Platform Admin</Text>
          </HStack>
          <HStack spacing={2}>
            <Button size="sm" leftIcon={<RefreshCw size={14} />} variant="ghost" onClick={loadOverview} isLoading={loading}>
              Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={() => loadAuditLog()}>
              Audit Log
            </Button>
          </HStack>
        </HStack>

        {/* Platform Stats */}
        {stats && (
          <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Active Orgs</StatLabel>
              <StatNumber>{stats.active_organizations}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Total Users</StatLabel>
              <StatNumber>{stats.total_users}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Active Subs</StatLabel>
              <StatNumber>{stats.active_subscriptions}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Posts (30d)</StatLabel>
              <StatNumber>{stats.posts_last_30_days}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Regens (30d)</StatLabel>
              <StatNumber>{stats.image_regens_last_30_days}</StatNumber>
            </Stat>
            <Stat bg="white" p={4} borderRadius="lg" borderWidth={1}>
              <StatLabel>Est. Revenue (30d)</StatLabel>
              <StatNumber>{fmtCost(stats.estimated_revenue_last_30_days_cents)}</StatNumber>
            </Stat>
          </SimpleGrid>
        )}

        {/* Organizations Table */}
        <Box bg="white" p={4} borderRadius="lg" borderWidth={1}>
          <HStack mb={3}>
            <Building2 size={18} />
            <Text fontWeight="600">Organizations</Text>
          </HStack>
          <Box overflowX="auto">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Status</Th>
                  <Th isNumeric>Members</Th>
                  <Th isNumeric>Posts / Included</Th>
                  <Th isNumeric>Overage</Th>
                  <Th isNumeric>Regens</Th>
                  <Th isNumeric>Upcoming Invoice</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {orgs.map(o => (
                  <Tr key={o.id} _hover={{ bg: 'gray.50', cursor: 'pointer' }} onClick={() => loadOrgDetail(o.id)}>
                    <Td fontWeight="500">{o.name}</Td>
                    <Td>
                      <Badge colorScheme={o.subscription_status === 'active' ? 'green' : o.subscription_status === 'past_due' ? 'red' : 'gray'}>
                        {o.subscription_status}
                      </Badge>
                    </Td>
                    <Td isNumeric>{o.member_count}</Td>
                    <Td isNumeric>{o.posts_this_period} / {o.included_posts}</Td>
                    <Td isNumeric>{o.overage_posts > 0 ? <Text color="orange.500" fontWeight="600">{o.overage_posts}</Text> : '0'}</Td>
                    <Td isNumeric>{o.image_regens_this_period}</Td>
                    <Td isNumeric>{o.upcoming_invoice_cents ? fmtCost(o.upcoming_invoice_cents) : '—'}</Td>
                    <Td fontSize="xs" color="brand.500">View →</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Box>

        <Divider />
        <Text fontSize="xs" color="gray.400" textAlign="center">
          Platform-wide data. Click an organization for details.
        </Text>
      </VStack>
    </Box>
  );
}
